import { useState, useEffect } from "react";
import { customerbaseddevices, customerbasedshift } from "../../Services/app/operatorservice";
import { useUserRole } from "./useUserRole";
import { filterGroupsForUserWithPlants, filterPlantsForUser, resolveAvailableGroupCodes } from "../utils/machineGroupAccess";

const naturalSort = (arr) =>
  [...arr].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

export const useMachineGroups = (customerId, singleSelect = false) => {
  const [devices, setDevices] = useState([]);
  const [deviceNameID, setDeviceNameID] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);
  const [groupsRaw, setGroupsRaw] = useState([]);
  const [plantsRaw, setPlantsRaw] = useState([]);
  const [availableMachines, setAvailableMachines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState(singleSelect ? "" : []);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedPlants, setSelectedPlants] = useState([]);
  const [loading, setLoading] = useState(true);

  const { isSuperAdmin, userRole } = useUserRole();
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const groupsOnly = Array.isArray(userDetails?.groups) ? userDetails.groups : null;
  const hasGroupsAccess = !isSuperAdmin && Array.isArray(groupsOnly) && groupsOnly.length > 0;
  const userPlantCodes = Array.isArray(userDetails?.plantAccess) ? userDetails.plantAccess : [];

  // Plant Group access-filtered for this user — a Plant filter above Machine
  // Group, PMI-only in practice since only PMI configures a `plantgroups`
  // attribute. Empty for every other customer, in which case everything
  // below collapses to today's plain Machine Group behavior.
  const plantGroups = filterPlantsForUser(plantsRaw, userRole, userPlantCodes);
  const plantOptions = plantGroups.map((p) => ({ value: p.code, label: p.name || p.code }));
  const showPlantFilter = plantGroups.length > 1;

  const fetchDevices = async () => {
    try {
      const result = await customerbaseddevices(customerId, 100, 0);
      const devicesList = result.data || [];
      setDevices(devicesList);
      const deviceNameId = devicesList.map((d) => ({
        id: d?.id?.id,
        name: d?.name,
      }));
      setDeviceNameID(deviceNameId);
      return devicesList;
    } catch (err) {
      console.error("Failed to fetch devices", err);
      return [];
    }
  };

  const fetchPlantGroups = async () => {
    try {
      const result = await customerbasedshift(customerId, "plantgroups");
      return result?.[0]?.value ?? [];
    } catch (error) {
      console.error("Error fetching plant groups:", error);
      return [];
    }
  };

  // Applies the current group list to derive selectedGroups/availableMachines/
  // selectedMachines — shared by the initial fetch and by handlePlantChange so
  // both stay in lock-step with the same single/multi/none selection rules.
  const deriveSelectionFromGroups = (groupsList, devicesList) => {
    const liveDeviceNames = new Set(devicesList.map((d) => d?.name).filter(Boolean));
    const onlyLive = (names) => (names || []).filter((m) => liveDeviceNames.has(m));

    if (groupsList.length === 1) {
      const singleGroup = groupsList[0];
      const machines = naturalSort(onlyLive(singleGroup.machines));
      setSelectedGroups([singleGroup.name]);
      setAvailableMachines(machines);
      setSelectedMachines(singleSelect ? machines[0] || "" : machines);
    } else if (groupsList.length > 1) {
      const allMachines = naturalSort(onlyLive(groupsList.flatMap((g) => g.machines || [])));
      const allGroupNames = groupsList.map((g) => g.name);
      setSelectedGroups(allGroupNames);
      setAvailableMachines(allMachines);
      setSelectedMachines(singleSelect ? allMachines[0] || "" : allMachines);
    } else {
      const deviceNames = naturalSort(devicesList.map((d) => d.name).filter(Boolean));
      setSelectedGroups([]);
      setAvailableMachines(deviceNames);
      setSelectedMachines(singleSelect ? deviceNames[0] || "" : deviceNames);
    }
  };

  const fetchMachineGroups = async (devicesList = []) => {
    try {
      const result = await customerbasedshift(customerId, "machinegroups");
      const groupsRawList = result?.[0]?.value ?? [];
      const plantsRawList = await fetchPlantGroups();
      setGroupsRaw(groupsRawList);
      setPlantsRaw(plantsRawList);

      // Users assigned to specific cell groups (set during registration), or
      // reachable through a Plant they're assigned, see the union of both;
      // users with no assigned groups AND no plant access see everything.
      const accessFilteredGroups = filterGroupsForUserWithPlants(
        groupsRawList, userRole, groupsOnly, plantsRawList, userPlantCodes
      );

      const plantsForUser = filterPlantsForUser(plantsRawList, userRole, userPlantCodes);
      const allPlantCodes = plantsForUser.map((p) => p.code);
      setSelectedPlants(allPlantCodes);

      const narrowedCodes = resolveAvailableGroupCodes(accessFilteredGroups, plantsForUser, allPlantCodes);
      const narrowedGroups = accessFilteredGroups.filter((g) => narrowedCodes.includes(g.code));

      setMachineGroups(narrowedGroups);
      deriveSelectionFromGroups(narrowedGroups, devicesList);

      return narrowedGroups;
    } catch (error) {
      console.error("Error fetching machine groups:", error);
      const deviceNames = naturalSort(devicesList.map((d) => d.name).filter(Boolean));
      setGroupsRaw([]);
      setPlantsRaw([]);
      setSelectedPlants([]);
      setMachineGroups([]);
      setSelectedGroups([]);
      setAvailableMachines(deviceNames);
      setSelectedMachines(singleSelect ? deviceNames[0] || "" : deviceNames);
      return [];
    }
  };

  const initialize = async () => {
    setLoading(true);
    const devicesList = await fetchDevices();
    await fetchMachineGroups(devicesList);
    setLoading(false);
  };

  const handleGroupChange = (selectedGroupNames = []) => {
    let updatedGroups = [...selectedGroupNames];
    if (selectedGroupNames.includes("all")) {
      if (selectedGroups.length === machineGroups.length) {
        updatedGroups = [];
      } else {
        updatedGroups = machineGroups.map((g) => g.name);
      }
    }
    setSelectedGroups(updatedGroups);
    if (updatedGroups.length === 0) {
      setAvailableMachines([]);
      setSelectedMachines(singleSelect ? "" : []);
      return;
    }
    // Same live-device cross-reference as fetchMachineGroups above — keeps a
    // stale/deleted machine name from reappearing when the group selection changes.
    const liveDeviceNames = new Set(devices.map((d) => d?.name).filter(Boolean));
    const selectedMachinesCombined = naturalSort(
      machineGroups
        .filter((g) => updatedGroups.includes(g.name))
        .flatMap((g) => g.machines || [])
        .filter((m) => liveDeviceNames.has(m))
    );
    setAvailableMachines(selectedMachinesCombined);
    setSelectedMachines(
      singleSelect ? selectedMachinesCombined[0] || "" : selectedMachinesCombined
    );
  };

  // Narrows Machine Group by the selected Plant(s), same "all" toggle
  // convention as handleGroupChange, then re-derives the group selection —
  // does not refetch, mirrors handleGroupChange's client-side-only update.
  const handlePlantChange = (rawValue = []) => {
    let nextPlantCodes = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
    if (Array.isArray(nextPlantCodes) && nextPlantCodes.includes('all')) {
      const allCodes = plantGroups.map((p) => p.code);
      const currentlyAllSelected = selectedPlants.length === allCodes.length;
      nextPlantCodes = currentlyAllSelected ? [] : allCodes;
    }
    setSelectedPlants(nextPlantCodes);

    // Access filtering itself never changes on plant re-selection — only
    // which of those already-accessible groups are visible — so recompute
    // from the stable raw list (groupsRaw), not from `machineGroups` (which
    // is already narrowed by whatever was selected before).
    const accessFilteredGroups = filterGroupsForUserWithPlants(
      groupsRaw, userRole, groupsOnly, plantsRaw, userPlantCodes
    );
    const narrowedCodes = resolveAvailableGroupCodes(accessFilteredGroups, plantGroups, nextPlantCodes);
    const narrowedGroups = accessFilteredGroups.filter((g) => narrowedCodes.includes(g.code));

    setMachineGroups(narrowedGroups);
    deriveSelectionFromGroups(narrowedGroups, devices);
  };

  const handleMachineChange = (selectedValue) => {
    if (singleSelect) {
      setSelectedMachines(selectedValue);
    } else {
      const selectedMachineValues = selectedValue;
      if (selectedMachineValues.includes("all")) {
        if (selectedMachines.length === availableMachines.length) {
          setSelectedMachines([]);
        } else {
          setSelectedMachines(availableMachines);
        }
        return;
      }
      setSelectedMachines(selectedMachineValues);
    }
  };

  const getDeviceObjectsForMachines = (machineNames = []) => {
    if (!Array.isArray(machineNames) || machineNames.length === 0) return [];
    return devices.filter((d) => machineNames.includes(d.name));
  };

  const isAllMachinesSelected =
    !singleSelect &&
    selectedMachines.length === availableMachines.length &&
    availableMachines.length > 0;

  const showMachineGroupsDropdown = machineGroups.length > 1;

  useEffect(() => {
    if (customerId) {
      initialize();
    }
  }, [customerId, isSuperAdmin]);

  return {
    devices,
    deviceNameID,
    machineGroups,
    availableMachines,
    selectedMachines,
    selectedGroups,
    loading,
    hasGroupsAccess,
    isAllMachinesSelected,
    showMachineGroupsDropdown,
    handleGroupChange,
    handleMachineChange,
    setSelectedMachines,
    getDeviceObjectsForMachines,
    refreshMachineGroups: fetchMachineGroups,

    plantGroups,
    plantOptions,
    selectedPlants,
    showPlantFilter,
    handlePlantChange,
  };
};
